import type {
  ApiResponse,
  ApiError,
  AuthResponse,
  User,
  Student,
  Task,
  CalendarEvent,
  SchoolYear,
  Vacation,
  Period,
  Notification,
  Subject,
  StudentSchoolYear,
  Grade,
  LoginCredentials,
  RegisterCredentials,
  CreateStudentInput,
  UpdateStudentInput,
  CreateTaskInput,
  UpdateTaskInput,
  CreateCalendarEventInput,
  UpdateCalendarEventInput,
  CreateSchoolYearInput,
  CreateVacationInput,
  CreatePeriodInput,
  UpdatePeriodInput,
  CreatePeriodsInput,
  CreateSubjectInput,
  UpdateSubjectInput,
  CreateStudentSchoolYearInput,
  CreateGradeInput,
  UpdateGradeInput,
  AIExtractedTask,
  AICalendarExtractionResult,
  AIExtractedSubject,
  AIScheduleExtractionResult,
  AIGradeExtractionResult,
  AIHomeworkHelpRequest,
  AIHomeworkHelpResponse,
  AIWeeklySummaryRequest,
  AIWeeklySummaryResponse,
  AIStudyPlanRequest,
  AIStudyPlanResponse,
  StudyPlan,
  BackupData,
  BackupValidationResult,
  BackupImportResult,
  SubscriptionStatusResponse,
  UsageQuota,
  FamilyMember,
  Uniform,
  UniformSchedule,
  Holiday,
  UniformResolutionMap,
  CreateUniformInput,
  UpdateUniformInput,
  BatchCreateUniformsInput,
  UniformScheduleUpsertInput,
  CopyUniformScheduleInput,
  CreateHolidayInput,
  UpdateHolidayInput,
  BatchCreateHolidaysInput,
  UniformScheduleMode,
  AIUniformExtractionResult,
  AISchoolYearExtractionResult,
} from '@frappi/shared';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

// Custom error class that preserves API error codes
export class ApiRequestError extends Error {
  code?: string;
  usageType?: string;
  remaining?: number;

  constructor(message: string, code?: string, usageType?: string, remaining?: number) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = code;
    this.usageType = usageType;
    this.remaining = remaining;
  }

  isQuotaExceeded(): boolean {
    return this.code === 'QUOTA_EXCEEDED';
  }
}

class ApiClient {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    });

    let data;
    try {
      data = await response.json();
    } catch {
      throw new Error('Invalid response format from server');
    }

    if (!response.ok) {
      const error = data as ApiError & { code?: string; usageType?: string; remaining?: number };
      throw new ApiRequestError(
        error.message || 'An error occurred',
        error.code,
        error.usageType,
        error.remaining
      );
    }

    // Validate response structure before type assertion
    if (data && typeof data === 'object' && 'data' in data) {
      return (data as ApiResponse<T>).data;
    }
    throw new Error('Invalid API response structure');
  }

  private async requestPaginated<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<{ data: T[]; pagination: { page: number; pageSize: number; total: number; totalPages: number } }> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    });

    let result;
    try {
      result = await response.json();
    } catch {
      throw new Error('Invalid response format from server');
    }

    if (!response.ok) {
      const error = result as ApiError & { code?: string; usageType?: string; remaining?: number };
      throw new ApiRequestError(
        error.message || 'An error occurred',
        error.code,
        error.usageType,
        error.remaining
      );
    }

    return {
      data: result.data,
      pagination: result.pagination,
    };
  }

  // Auth endpoints
  auth = {
    register: (credentials: RegisterCredentials) =>
      this.request<AuthResponse>('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify(credentials),
      }),

    login: (credentials: LoginCredentials) =>
      this.request<AuthResponse>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
      }),

    logout: () =>
      this.request<{ message: string }>('/api/auth/logout', {
        method: 'POST',
      }),

    me: () => this.request<User>('/api/auth/me'),

    refresh: () => this.request<AuthResponse>('/api/auth/refresh', {
      method: 'POST',
    }),

    updatePassword: (currentPassword: string, newPassword: string) =>
      this.request<{ message: string }>('/api/auth/password', {
        method: 'PUT',
        body: JSON.stringify({ currentPassword, newPassword }),
      }),

    updateProfile: (updates: { name?: string; avatarUrl?: string; preferences?: Record<string, unknown> }) =>
      this.request<User>('/api/auth/profile', {
        method: 'PUT',
        body: JSON.stringify(updates),
      }),

    deleteAccount: (password: string) =>
      this.request<{ message: string }>('/api/auth/account', {
        method: 'DELETE',
        body: JSON.stringify({ password }),
      }),

    forgotPassword: (email: string) =>
      this.request<{ message: string }>('/api/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),

    resetPassword: (email: string, code: string, newPassword: string) =>
      this.request<{ message: string }>('/api/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ email, code, newPassword }),
      }),
  };

  // Students endpoints
  students = {
    list: () => this.request<Student[]>('/api/students'),

    get: (id: string) => this.request<Student>(`/api/students/${id}`),

    create: (data: CreateStudentInput) =>
      this.request<Student>('/api/students', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: UpdateStudentInput) =>
      this.request<Student>(`/api/students/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      this.request<{ message: string }>(`/api/students/${id}`, {
        method: 'DELETE',
      }),

    getTasks: (id: string) => this.request<Task[]>(`/api/students/${id}/tasks`),

    getEvents: (id: string) => this.request<CalendarEvent[]>(`/api/students/${id}/events`),
  };

  // Tasks endpoints
  tasks = {
    list: (filters?: {
      status?: string;
      priority?: string;
      studentId?: string;
      category?: string;
      assignedTo?: string;
      dueBefore?: string;
      dueAfter?: string;
      search?: string;
      page?: number;
      pageSize?: number;
    }) => {
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined) {
            params.append(key, String(value));
          }
        });
      }
      const query = params.toString();
      return this.requestPaginated<Task>(
        `/api/tasks${query ? `?${query}` : ''}`
      );
    },

    get: (id: string) => this.request<Task>(`/api/tasks/${id}`),

    create: (data: CreateTaskInput) =>
      this.request<Task>('/api/tasks', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: UpdateTaskInput) =>
      this.request<Task>(`/api/tasks/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      this.request<{ message: string }>(`/api/tasks/${id}`, {
        method: 'DELETE',
      }),

    complete: (id: string) =>
      this.request<Task>(`/api/tasks/${id}/complete`, {
        method: 'PUT',
      }),

    updateStatus: (id: string, status: string) =>
      this.request<Task>(`/api/tasks/${id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      }),

    getOverdue: () => this.request<Task[]>(`/api/tasks/overdue?tzOffset=${new Date().getTimezoneOffset()}`),

    getUpcoming: () => this.request<Task[]>(`/api/tasks/upcoming?tzOffset=${new Date().getTimezoneOffset()}`),
  };

  // Calendar endpoints
  calendar = {
    listEvents: (filters?: {
      startDate?: string;
      endDate?: string;
      studentId?: string;
      eventType?: string;
    }) => {
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined) {
            params.append(key, String(value));
          }
        });
      }
      const query = params.toString();
      return this.request<CalendarEvent[]>(
        `/api/calendar/events${query ? `?${query}` : ''}`
      );
    },

    getEvent: (id: string) => this.request<CalendarEvent>(`/api/calendar/events/${id}`),

    createEvent: (data: CreateCalendarEventInput) =>
      this.request<CalendarEvent>('/api/calendar/events', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateEvent: (id: string, data: UpdateCalendarEventInput) =>
      this.request<CalendarEvent>(`/api/calendar/events/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    deleteEvent: (id: string) =>
      this.request<{ message: string }>(`/api/calendar/events/${id}`, {
        method: 'DELETE',
      }),

    getDay: (date: string) =>
      this.request<{ date: string; events: CalendarEvent[]; tasks: Task[] }>(
        `/api/calendar/day/${date}`
      ),

    getWeek: (date: string) =>
      this.request<{ startDate: string; endDate: string; events: CalendarEvent[]; tasks: Task[] }>(
        `/api/calendar/week/${date}`
      ),

    getMonth: (date: string) =>
      this.request<{ startDate: string; endDate: string; events: CalendarEvent[]; tasks: Task[] }>(
        `/api/calendar/month/${date}`
      ),
  };

  // Files endpoints
  files = {
    upload: async (file: File): Promise<{
      key: string;
      name: string;
      size: number;
      type: string;
      url: string;
    }> => {
      const formData = new FormData();
      formData.append('file', file);

      const headers: HeadersInit = {};
      if (this.token) {
        headers['Authorization'] = `Bearer ${this.token}`;
      }

      const response = await fetch(`${API_BASE_URL}/api/files/upload`, {
        method: 'POST',
        headers,
        body: formData,
        credentials: 'include',
      });

      let data;
      try {
        data = await response.json();
      } catch {
        throw new Error('Invalid response format from server');
      }

      if (!response.ok) {
        throw new Error(data.error || 'Upload failed');
      }

      return data.data;
    },

    uploadMultiple: async (files: File[]): Promise<{
      data: Array<{
        key: string;
        name: string;
        size: number;
        type: string;
        url: string;
      }>;
      errors?: Array<{ name: string; error: string }>;
    }> => {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));

      const headers: HeadersInit = {};
      if (this.token) {
        headers['Authorization'] = `Bearer ${this.token}`;
      }

      const response = await fetch(`${API_BASE_URL}/api/files/upload-multiple`, {
        method: 'POST',
        headers,
        body: formData,
        credentials: 'include',
      });

      let result;
      try {
        result = await response.json();
      } catch {
        throw new Error('Invalid response format from server');
      }

      if (!response.ok) {
        throw new Error(result.error || 'Upload failed');
      }

      return { data: result.data, errors: result.errors };
    },

    getUrl: (key: string) => `${API_BASE_URL}/api/files/${key}`,

    delete: (key: string) =>
      this.request<{ message: string }>(`/api/files/${encodeURIComponent(key)}`, {
        method: 'DELETE',
      }),
  };

  // School years endpoints
  schoolYears = {
    list: () => this.request<SchoolYear[]>('/api/school-years'),

    get: (id: string) => this.request<SchoolYear & { vacations: Vacation[] }>(`/api/school-years/${id}`),

    create: (data: CreateSchoolYearInput) =>
      this.request<SchoolYear>('/api/school-years', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: Partial<CreateSchoolYearInput>) =>
      this.request<SchoolYear>(`/api/school-years/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      this.request<{ message: string }>(`/api/school-years/${id}`, {
        method: 'DELETE',
      }),

    activate: (id: string) =>
      this.request<SchoolYear>(`/api/school-years/${id}/activate`, {
        method: 'PUT',
      }),

    getVacations: (id: string) => this.request<Vacation[]>(`/api/school-years/${id}/vacations`),

    getStudents: (id: string) => this.request<StudentSchoolYear[]>(`/api/school-years/${id}/students`),

    addStudent: (schoolYearId: string, data: Omit<CreateStudentSchoolYearInput, 'schoolYearId'>) =>
      this.request<StudentSchoolYear>(`/api/school-years/${schoolYearId}/students`, {
        method: 'POST',
        body: JSON.stringify({ ...data, schoolYearId }),
      }),

    removeStudent: (schoolYearId: string, studentId: string) =>
      this.request<{ message: string }>(`/api/school-years/${schoolYearId}/students/${studentId}`, {
        method: 'DELETE',
      }),
  };

  // Vacations endpoints
  vacations = {
    list: (schoolYearId?: string) => {
      const params = schoolYearId ? `?schoolYearId=${schoolYearId}` : '';
      return this.request<Vacation[]>(`/api/vacations${params}`);
    },

    get: (id: string) => this.request<Vacation>(`/api/vacations/${id}`),

    create: (data: CreateVacationInput) =>
      this.request<Vacation>('/api/vacations', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: Partial<CreateVacationInput>) =>
      this.request<Vacation>(`/api/vacations/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      this.request<{ message: string }>(`/api/vacations/${id}`, {
        method: 'DELETE',
      }),
  };

  // Periods endpoints
  periods = {
    listBySchoolYear: (schoolYearId: string) =>
      this.request<Period[]>(`/api/periods/school-year/${schoolYearId}`),

    get: (id: string) => this.request<Period>(`/api/periods/${id}`),

    create: (data: CreatePeriodInput) =>
      this.request<Period>('/api/periods', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    generate: (data: CreatePeriodsInput) =>
      this.request<Period[]>('/api/periods/generate', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: UpdatePeriodInput) =>
      this.request<Period>(`/api/periods/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      this.request<{ message: string }>(`/api/periods/${id}`, {
        method: 'DELETE',
      }),

    deleteAllForSchoolYear: (schoolYearId: string) =>
      this.request<{ message: string }>(`/api/periods/school-year/${schoolYearId}`, {
        method: 'DELETE',
      }),
  };

  // Notifications endpoints
  notifications = {
    list: (filters?: { unreadOnly?: boolean; page?: number; pageSize?: number }) => {
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined) {
            params.append(key, String(value));
          }
        });
      }
      const query = params.toString();
      return this.requestPaginated<Notification>(
        `/api/notifications${query ? `?${query}` : ''}`
      );
    },

    get: (id: string) => this.request<Notification>(`/api/notifications/${id}`),

    getUnreadCount: () => this.request<{ count: number }>('/api/notifications/unread-count'),

    markAsRead: (id: string) =>
      this.request<Notification>(`/api/notifications/${id}/read`, {
        method: 'PUT',
      }),

    markAsUnread: (id: string) =>
      this.request<Notification>(`/api/notifications/${id}/unread`, {
        method: 'PUT',
      }),

    markAllAsRead: () =>
      this.request<{ message: string }>('/api/notifications/read-all', {
        method: 'PUT',
      }),

    delete: (id: string) =>
      this.request<{ message: string }>(`/api/notifications/${id}`, {
        method: 'DELETE',
      }),

    bulk: (input: { action: 'mark_read' | 'mark_unread' | 'delete'; ids?: string[]; all?: boolean }) =>
      this.request<{ affected: number }>('/api/notifications/bulk', {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    testUpcoming: () =>
      this.request<{ inApp: boolean; push: number; reason?: string }>('/api/notifications/test-upcoming', {
        method: 'POST',
      }),

    testUniform: (mode: 'next-day' | 'weekly') =>
      this.request<{ inApp: boolean; push: number; reason?: string }>('/api/notifications/test-uniform', {
        method: 'POST',
        body: JSON.stringify({ mode }),
      }),
  };

  // Subjects endpoints
  subjects = {
    list: (studentId?: string) => {
      const params = studentId ? `?studentId=${studentId}` : '';
      return this.request<Subject[]>(`/api/subjects${params}`);
    },

    get: (id: string) => this.request<Subject>(`/api/subjects/${id}`),

    create: (data: CreateSubjectInput) =>
      this.request<Subject>('/api/subjects', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: UpdateSubjectInput) =>
      this.request<Subject>(`/api/subjects/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      this.request<{ message: string }>(`/api/subjects/${id}`, {
        method: 'DELETE',
      }),
  };

  // Grades endpoints
  grades = {
    list: (filters?: {
      studentId?: string;
      subjectId?: string;
      periodId?: string;
      gradeType?: string;
      dateFrom?: string;
      dateTo?: string;
      search?: string;
      page?: number;
      pageSize?: number;
    }) => {
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined) {
            params.append(key, String(value));
          }
        });
      }
      const query = params.toString();
      return this.requestPaginated<Grade>(
        `/api/grades${query ? `?${query}` : ''}`
      );
    },

    get: (id: string) => this.request<Grade>(`/api/grades/${id}`),

    create: (data: CreateGradeInput) =>
      this.request<Grade>('/api/grades', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: UpdateGradeInput) =>
      this.request<Grade>(`/api/grades/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      this.request<{ message: string }>(`/api/grades/${id}`, {
        method: 'DELETE',
      }),

    getStats: (studentId: string) =>
      this.request<{
        overallAverage: number;
        totalGrades: number;
        subjectAverages: Array<{
          id: string;
          name: string;
          color: string;
          average: number;
          count: number;
        }>;
        recentGrades: Grade[];
      }>(`/api/grades/stats/${studentId}`),
  };

  // Dashboard endpoints
  dashboard = {
    getStats: () =>
      this.request<{
        stats: {
          pendingTasks: number;
          students: number;
          todayEvents: number;
          overdueTasks: number;
        };
        upcomingTasks: Array<{
          id: string;
          title: string;
          dueDate: string | null;
          dueTime: string | null;
          priority: string;
          status: string;
          student?: {
            id: string;
            name: string | null;
            color: string | null;
          };
        }>;
        todayEvents: Array<{
          id: string;
          title: string;
          startTime: string | null;
          endTime: string | null;
          eventType: string;
          color: string | null;
          allDay: boolean;
          student?: {
            id: string;
            name: string | null;
            color: string | null;
          };
        }>;
      }>(`/api/dashboard/stats?tzOffset=${new Date().getTimezoneOffset()}`),
  };

  // AI endpoints
  ai = {
    scanTask: (fileKey: string) =>
      this.request<{ extracted: AIExtractedTask; fileKey: string }>('/api/ai/scan-task', {
        method: 'POST',
        body: JSON.stringify({ fileKey }),
      }),

    scanCalendar: (fileKey: string) =>
      this.request<{ extracted: AICalendarExtractionResult; fileKey: string }>('/api/ai/scan-calendar', {
        method: 'POST',
        body: JSON.stringify({ fileKey }),
      }),

    batchCreateEvents: (events: CreateCalendarEventInput[]) =>
      this.request<CalendarEvent[]>('/api/ai/batch-create-events', {
        method: 'POST',
        body: JSON.stringify({ events }),
      }),

    getHomeworkHelp: (request: AIHomeworkHelpRequest) =>
      this.request<AIHomeworkHelpResponse>('/api/ai/homework-help', {
        method: 'POST',
        body: JSON.stringify(request),
      }),

    getWeeklySummary: (request: AIWeeklySummaryRequest) =>
      this.request<AIWeeklySummaryResponse>('/api/ai/weekly-summary', {
        method: 'POST',
        body: JSON.stringify(request),
      }),

    getSavedWeeklySummary: (studentId?: string) => {
      const params = studentId ? `?studentId=${studentId}` : '';
      return this.request<AIWeeklySummaryResponse | null>(`/api/ai/weekly-summary${params}`);
    },

    deleteWeeklySummary: (studentId?: string) => {
      const params = studentId ? `?studentId=${studentId}` : '';
      return this.request<{ message: string }>(`/api/ai/weekly-summary${params}`, {
        method: 'DELETE',
      });
    },

    getStudyPlan: (request: AIStudyPlanRequest) =>
      this.request<AIStudyPlanResponse>('/api/ai/study-plan', {
        method: 'POST',
        body: JSON.stringify(request),
      }),

    getSavedStudyPlan: (studentId?: string) => {
      const params = studentId ? `?studentId=${studentId}` : '';
      return this.request<StudyPlan | null>(`/api/ai/study-plan${params}`);
    },

    deleteStudyPlan: (studentId?: string) => {
      const params = studentId ? `?studentId=${studentId}` : '';
      return this.request<{ message: string }>(`/api/ai/study-plan${params}`, {
        method: 'DELETE',
      });
    },

    toggleStudySession: (data: { studentId?: string; date: string; sessionIndex: number; completed: boolean }) =>
      this.request<StudyPlan>('/api/ai/study-plan/session', {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    scanSubject: (fileKey: string) =>
      this.request<{ extracted: AIExtractedSubject; fileKey: string }>('/api/ai/scan-subject', {
        method: 'POST',
        body: JSON.stringify({ fileKey }),
      }),

    scanSchedule: (fileKey: string) =>
      this.request<{ extracted: AIScheduleExtractionResult; fileKey: string }>('/api/ai/scan-schedule', {
        method: 'POST',
        body: JSON.stringify({ fileKey }),
      }),

    batchCreateSubjects: (subjects: CreateSubjectInput[]) =>
      this.request<Subject[]>('/api/ai/batch-create-subjects', {
        method: 'POST',
        body: JSON.stringify({ subjects }),
      }),

    scanGrade: (fileKey: string) =>
      this.request<{ extracted: AIGradeExtractionResult; fileKey: string }>('/api/ai/scan-grade', {
        method: 'POST',
        body: JSON.stringify({ fileKey }),
      }),

    batchCreateGrades: (grades: CreateGradeInput[]) =>
      this.request<Grade[]>('/api/ai/batch-create-grades', {
        method: 'POST',
        body: JSON.stringify({ grades }),
      }),

    voiceToData: (fileKey: string, type: 'task' | 'grade' | 'calendar') =>
      this.request<{ extracted: AIExtractedTask; fileKey: string; type: string }>('/api/ai/voice-to-data', {
        method: 'POST',
        body: JSON.stringify({ fileKey, type }),
      }),

    scanUniforms: (params: { fileKey: string; studentId: string; mode: UniformScheduleMode; cycleLength?: number }) =>
      this.request<{ extracted: AIUniformExtractionResult; fileKey: string }>('/api/ai/scan-uniforms', {
        method: 'POST',
        body: JSON.stringify(params),
      }),

    scanSchoolYear: (fileKey: string) =>
      this.request<{ extracted: AISchoolYearExtractionResult; fileKey: string }>('/api/ai/scan-school-year', {
        method: 'POST',
        body: JSON.stringify({ fileKey }),
      }),
  };

  // Uniforms endpoints
  uniforms = {
    list: (studentId: string) =>
      this.request<Uniform[]>(`/api/uniforms?studentId=${studentId}`),

    create: (data: CreateUniformInput) =>
      this.request<Uniform>('/api/uniforms', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    batchCreate: (data: BatchCreateUniformsInput) =>
      this.request<Uniform[]>('/api/uniforms/batch', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: UpdateUniformInput) =>
      this.request<Uniform>(`/api/uniforms/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      this.request<{ message: string }>(`/api/uniforms/${id}`, {
        method: 'DELETE',
      }),

    resolve: (params: { studentId: string; from: string; to: string }) =>
      this.request<UniformResolutionMap>(
        `/api/uniforms/resolve?studentId=${params.studentId}&from=${params.from}&to=${params.to}`
      ),
  };

  // Uniform schedules endpoints
  uniformSchedules = {
    get: (studentId: string) =>
      this.request<UniformSchedule | null>(`/api/uniform-schedules/${studentId}`),

    upsert: (studentId: string, data: UniformScheduleUpsertInput) =>
      this.request<UniformSchedule>(`/api/uniform-schedules/${studentId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (studentId: string) =>
      this.request<{ message: string }>(`/api/uniform-schedules/${studentId}`, {
        method: 'DELETE',
      }),

    copy: (data: CopyUniformScheduleInput) =>
      this.request<{ message: string; uniformsCopied: number }>('/api/uniform-schedules/copy', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  };

  // Holidays endpoints
  holidays = {
    list: (schoolYearId: string) =>
      this.request<Holiday[]>(`/api/holidays?schoolYearId=${schoolYearId}`),

    create: (data: CreateHolidayInput) =>
      this.request<Holiday>('/api/holidays', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    batchCreate: (data: BatchCreateHolidaysInput) =>
      this.request<Holiday[]>('/api/holidays/batch', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: UpdateHolidayInput) =>
      this.request<Holiday>(`/api/holidays/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      this.request<{ message: string }>(`/api/holidays/${id}`, {
        method: 'DELETE',
      }),
  };

  // Backup endpoints
  backup = {
    export: async (): Promise<Blob> => {
      const headers: HeadersInit = {};
      if (this.token) {
        headers['Authorization'] = `Bearer ${this.token}`;
      }

      const response = await fetch(`${API_BASE_URL}/api/backup/export`, {
        headers,
        credentials: 'include',
      });

      if (!response.ok) {
        let error;
        try {
          error = await response.json();
        } catch {
          throw new Error('Export failed');
        }
        throw new Error(error.message || 'Export failed');
      }

      return response.blob();
    },

    validate: (data: BackupData) =>
      this.request<BackupValidationResult>('/api/backup/validate', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    import: (data: BackupData) =>
      this.request<BackupImportResult>('/api/backup/import', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  };

  // Subscription endpoints
  subscription = {
    getStatus: () =>
      this.request<SubscriptionStatusResponse>('/api/subscription/status'),

    getUsage: () =>
      this.request<UsageQuota>('/api/subscription/usage'),

    getCancelInfo: () =>
      this.request<{
        message: string;
        playStoreUrl: string;
        currentPlan: string;
        currentPeriodEnd?: string;
        accessUntil?: string;
      }>('/api/subscription/cancel', {
        method: 'POST',
      }),

    getFamilyMembers: () =>
      this.request<FamilyMember[]>('/api/subscription/family/members'),

    inviteFamilyMember: (email: string) =>
      this.request<FamilyMember>('/api/subscription/family/invite', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),

    acceptFamilyInvite: (inviteId: string) =>
      this.request<FamilyMember>('/api/subscription/family/accept', {
        method: 'POST',
        body: JSON.stringify({ inviteId }),
      }),

    removeFamilyMember: (memberId: string) =>
      this.request<{ message: string }>(`/api/subscription/family/members/${memberId}`, {
        method: 'DELETE',
      }),
  };
}

export const api = new ApiClient();
